import { useEffect, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { STATUSES, STATUS_STYLES, formatCurrency, formatDate } from "../lib/constants";
import { Card } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import { GripVertical } from "lucide-react";

export default function KanbanPage() {
  const [byStatus, setByStatus] = useState(null);
  const nav = useNavigate();

  const load = () => {
    api.get("/customers").then((r) => {
      const map = Object.fromEntries(STATUSES.map((s) => [s, []]));
      for (const c of r.data) {
        if (map[c.status]) map[c.status].push(c);
      }
      setByStatus(map);
    });
  };

  useEffect(load, []);

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const { source, destination, draggableId } = result;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    // Optimistic update
    setByStatus((prev) => {
      const next = { ...prev };
      const item = next[source.droppableId][source.index];
      next[source.droppableId] = next[source.droppableId].filter((_, i) => i !== source.index);
      next[destination.droppableId] = [...next[destination.droppableId]];
      next[destination.droppableId].splice(destination.index, 0, { ...item, status: destination.droppableId });
      return next;
    });

    try {
      await api.patch(`/customers/${draggableId}/status`, { status: destination.droppableId });
      toast.success(`Durum güncellendi: ${destination.droppableId}`);
    } catch {
      toast.error("Güncellenemedi");
      load();
    }
  };

  if (!byStatus) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {STATUSES.map((s) => <Skeleton key={s} className="h-64" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Görev Panosu</div>
        <div className="text-2xl font-display font-semibold tracking-tight mt-1">İş Durumu · Kanban</div>
        <div className="text-sm text-muted-foreground mt-1">Kartları sürükleyerek müşterilerin iş durumunu güncelle</div>
      </div>

      <DragDropContext onDragEnd={onDragEnd}>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {STATUSES.map((status) => {
            const s = STATUS_STYLES[status];
            const items = byStatus[status];
            return (
              <div key={status} data-testid={`kanban-col-${status}`}>
                <div className="flex items-center gap-2 px-3 py-2 mb-3">
                  <span className={`h-2.5 w-2.5 rounded-full ${s.dot}`} />
                  <div className="text-xs uppercase tracking-widest font-semibold">{status}</div>
                  <div className="text-xs text-muted-foreground ml-auto">{items.length}</div>
                </div>

                <Droppable droppableId={status}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`min-h-[500px] rounded-xl border-2 border-dashed p-2 space-y-2 transition-colors duration-200 ${
                        snapshot.isDraggingOver ? "border-foreground/40 bg-muted/50" : "border-border bg-muted/20"
                      }`}
                    >
                      {items.length === 0 && (
                        <div className="text-xs text-center text-muted-foreground py-10">Boş</div>
                      )}
                      {items.map((c, idx) => (
                        <Draggable key={c.id} draggableId={c.id} index={idx}>
                          {(dp, snap) => (
                            <Card
                              ref={dp.innerRef}
                              {...dp.draggableProps}
                              {...dp.dragHandleProps}
                              onDoubleClick={() => nav(`/customers/${c.id}`)}
                              data-testid={`kanban-card-${c.id}`}
                              className={`p-3 cursor-grab active:cursor-grabbing transition-shadow duration-200 ${
                                snap.isDragging ? "shadow-lg rotate-1" : ""
                              }`}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0 flex-1">
                                  <div className="text-sm font-medium truncate">{c.first_name} {c.last_name}</div>
                                  <div className="text-xs text-muted-foreground truncate">{c.platform} · {c.package}</div>
                                </div>
                                <GripVertical className="h-4 w-4 text-muted-foreground/50 flex-shrink-0" />
                              </div>
                              <div className="mt-3 flex items-center justify-between text-xs">
                                <span className="font-mono">{formatCurrency(c.price)}</span>
                                <span className="text-muted-foreground">{formatDate(c.delivery_date)}</span>
                              </div>
                            </Card>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>
            );
          })}
        </div>
      </DragDropContext>
    </div>
  );
}
